<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <!-- 策略筛选条件：消耗日期 → 命名策略 → 其余（report-filter-panel 合并两行视觉，避免双 glass 断层） -->
      <div class="report-filter-panel">
      <el-form :inline="true" size="small" class="glass-form">
        <el-form-item :label="$t('common.consumptionDate')">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            style="width: 280px"
            :range-separator="$t('common.dateRangeSep')"
            :start-placeholder="$t('common.dateStart')"
            :end-placeholder="$t('common.dateEnd')"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item :label="$t('consumption.namingStrategy')">
          <el-select
            v-model="query.strategyId"
            :placeholder="$t('consumption.namingStrategyPlaceholder')"
            style="width: 200px"
            filterable
            clearable
            :filter-method="filterStrategySelect"
            @visible-change="onStrategySelectVisible"
            @change="handleQuery"
          >
            <el-option
              v-for="s in filteredStrategyList"
              :key="s.id"
              :label="strategyOptionLabel(s)"
              :value="s.id"
            >
              <span style="float: left">{{ s.strategyName }}</span>
              <span style="float: right; color: #909399; font-size: 12px; margin-left: 8px">
                {{ $t('strategyAnalysis.batchSuccessCount') }} {{ s.batchSuccessCount || 0 }}
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('strategyAnalysis.batchSuccessCount')">
          <el-input-number v-model="batchSuccessCountMin" :placeholder="$t('common.min')" size="mini" :controls="false" :min="0" style="width: 70px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="batchSuccessCountMax" :placeholder="$t('common.max')" size="mini" :controls="false" :min="0" style="width: 70px" />
        </el-form-item>
      </el-form>
      <el-form :inline="true" size="small" class="glass-form">
        <el-form-item :label="$t('common.bookName')">
          <el-input v-model="query.bookName" :placeholder="$t('common.bookNameSearchPlaceholder')" clearable size="small" style="width: 160px" @keyup.enter.native="handleQuery" />
        </el-form-item>
        <el-form-item v-if="!isDataLimited" :label="$t('bookSummary.operatorName')">
          <el-select v-model="query.userName" clearable filterable :placeholder="$t('bookSummary.operatorPlaceholder')" style="width: 150px">
            <el-option v-for="name in creatorOptions" :key="name" :label="name" :value="name" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('strategyAnalysis.account')">
          <el-input v-model="query.adAccountId" :placeholder="$t('strategyAnalysis.accountPlaceholder')" clearable size="small" style="width: 160px" @keyup.enter.native="handleQuery" />
        </el-form-item>
        <el-form-item :label="$t('insights.dimCampaign')">
          <el-input v-model="query.campaignKeyword" :placeholder="$t('strategyAnalysis.campaignPlaceholder')" clearable size="small" style="width: 160px" @keyup.enter.native="handleQuery" />
        </el-form-item>
        <el-form-item :label="$t('insights.dimAdset')" v-if="activeDimension === 'adset' || activeDimension === 'ad'">
          <el-input v-model="query.adsetKeyword" :placeholder="$t('strategyAnalysis.adsetPlaceholder')" clearable size="small" style="width: 160px" @keyup.enter.native="handleQuery" />
        </el-form-item>
        <el-form-item :label="$t('insights.dimAd')" v-if="activeDimension === 'ad'">
          <el-input v-model="query.adKeyword" :placeholder="$t('strategyAnalysis.adPlaceholder')" clearable size="small" style="width: 160px" @keyup.enter.native="handleQuery" />
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="showMetricFilter = !showMetricFilter">
            {{ showMetricFilter ? $t('bookSummary.hideFilter') : $t('bookSummary.moreFilter') }}
            <i :class="showMetricFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">{{ $t('common.reset') }}</el-button>
          <el-button type="warning" plain icon="el-icon-download" size="mini" :loading="exporting" @click="handleExport">{{ $t('common.export') }}</el-button>
        </el-form-item>
      </el-form>
      </div>

      <!-- 指标范围筛选 -->
      <el-form v-show="showMetricFilter" :inline="true" size="small" class="glass-form metric-filter-row">
        <el-form-item :label="$t('bookSummary.spend')">
          <el-input-number v-model="query.spendMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.spendMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item label="CTR">
          <el-input-number v-model="ctrMinPct" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="ctrMaxPct" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="2" style="width: 80px" />
          <span class="range-unit">%</span>
        </el-form-item>
        <el-form-item :label="$t('bookSummary.purchases')">
          <el-input-number v-model="query.purchasesMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="0" style="width: 80px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchasesMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="0" style="width: 80px" />
        </el-form-item>
        <el-form-item :label="$t('strategyAnalysis.costPerPurchaseShort')">
          <el-input-number v-model="query.costPerPurchaseMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.costPerPurchaseMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('bookSummary.purchaseValue')">
          <el-input-number v-model="query.purchaseValueMin" :placeholder="$t('common.min')" size="mini" :controls="false" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.purchaseValueMax" :placeholder="$t('common.max')" size="mini" :controls="false" style="width: 90px" />
        </el-form-item>
        <el-form-item :label="$t('bookSummary.roi')">
          <el-input-number v-model="query.roiMin" :placeholder="$t('common.min')" size="mini" :controls="false" :precision="4" style="width: 90px" />
          <span class="range-sep">~</span>
          <el-input-number v-model="query.roiMax" :placeholder="$t('common.max')" size="mini" :controls="false" :precision="4" style="width: 90px" />
        </el-form-item>
      </el-form>

      <!-- 策略概览统计卡片 -->
      <div class="stats-cards" v-if="selectedStrategy">
        <div class="stats-card">
          <div class="stats-label">{{ $t('strategyAnalysis.strategyName') }}</div>
          <div class="stats-value text-primary">{{ selectedStrategy.strategyName }}</div>
        </div>
        <div class="stats-card">
          <div class="stats-label">{{ $t('strategyAnalysis.batchSuccessCount') }}</div>
          <div class="stats-value">{{ selectedStrategy.batchSuccessCount || 0 }}</div>
        </div>
        <div class="stats-card" v-if="summary.spend > 0">
          <div class="stats-label">{{ $t('strategyAnalysis.totalSpend') }}</div>
          <div class="stats-value text-danger">{{ formatMoney(summary.spend) }}</div>
        </div>
        <div class="stats-card" v-if="summary.purchases > 0">
          <div class="stats-label">{{ $t('strategyAnalysis.totalPurchases') }}</div>
          <div class="stats-value">{{ formatInt(summary.purchases) }}</div>
        </div>
        <div class="stats-card" v-if="summary.purchaseValue > 0">
          <div class="stats-label">{{ $t('bookSummary.purchaseValue') }}</div>
          <div class="stats-value text-success">{{ formatMoney(summary.purchaseValue) }}</div>
        </div>
      </div>

      <!-- 维度切换：策略 → 账户 → 系列 → 广告组 → 广告 -->
      <el-tabs v-model="activeDimension" @tab-click="handleTabChange" style="margin-top: 5px;">
        <el-tab-pane :label="$t('strategyAnalysis.dimStrategy')" name="strategy" />
        <el-tab-pane :label="$t('insights.dimAccount')" name="account" />
        <el-tab-pane :label="$t('insights.dimCampaign')" name="campaign" />
        <el-tab-pane :label="$t('insights.dimAdset')" name="adset" />
        <el-tab-pane :label="$t('insights.dimAd')" name="ad" />
      </el-tabs>

      <!-- 数据表格 -->
      <el-table
        :key="'strategy-analysis-' + activeDimension"
        v-loading="loading"
        :data="tableData"
        border
        size="small"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
        @sort-change="handleSortChange"
      >
        <el-table-column type="index" label="#" width="50" align="center" />

        <!-- 策略维度：每行对应一个命名策略汇总 -->
        <el-table-column v-if="activeDimension === 'strategy'" :label="$t('strategyAnalysis.strategyName')" prop="strategyName" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.strategyName || (selectedStrategy && selectedStrategy.strategyName) || '-' }}</template>
        </el-table-column>

        <!-- 账户维度（策略分析：按命名策略+账户聚合） -->
        <el-table-column v-if="activeDimension === 'account'" :label="$t('strategyAnalysis.strategyName')" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.strategyName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'account'" :label="$t('insights.adAccountId')" width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.adAccountId || row.ad_account_id || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'account'" :label="$t('insights.adAccountName')" prop="adAccountName" min-width="160" show-overflow-tooltip />

        <!-- 系列维度 -->
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('strategyAnalysis.strategyName')" width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.strategyName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('insights.adAccountId')" prop="adAccountId" width="130" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('insights.adAccountName')" prop="adAccountName" width="160" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('insights.campaignIdCol')" prop="campaignId" min-width="160" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('insights.campaignName')" prop="campaignName" min-width="180" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('common.bookName')" prop="bookName" min-width="160" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'campaign'" :label="$t('bookSummary.operatorName')" prop="createUserName" width="100" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.createUserName || '-' }}</template>
        </el-table-column>

        <!-- 广告组维度 -->
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('strategyAnalysis.strategyName')" width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.strategyName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('insights.adAccountId')" prop="adAccountId" width="130" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('insights.adAccountName')" prop="adAccountName" width="140" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('insights.campaignName')" prop="campaignName" min-width="180" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('common.bookName')" prop="bookName" min-width="160" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('bookSummary.operatorName')" prop="createUserName" width="100" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.createUserName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'adset'" :label="$t('insights.adsetIdCol')" prop="adsetId" min-width="160" show-overflow-tooltip />

        <!-- 广告维度 -->
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('strategyAnalysis.strategyName')" width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.strategyName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('insights.adAccountId')" prop="adAccountId" width="120" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('insights.adAccountName')" prop="adAccountName" width="130" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('insights.campaignName')" prop="campaignName" min-width="180" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('common.bookName')" prop="bookName" min-width="160" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('bookSummary.operatorName')" prop="createUserName" width="100" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.createUserName || '-' }}</template>
        </el-table-column>
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('insights.adsetIdCol')" prop="adsetId" width="130" show-overflow-tooltip />
        <el-table-column v-if="activeDimension === 'ad'" :label="$t('insights.adIdCol')" prop="adId" min-width="140" show-overflow-tooltip />

        <!-- 指标列 -->
        <el-table-column :label="$t('bookSummary.spend')" width="110" align="right" sortable="custom" prop="spend">
          <template slot-scope="{ row }">{{ formatMoney(row.spend) }}</template>
        </el-table-column>
        <el-table-column :label="$t('strategyAnalysis.impressions')" width="100" align="right" sortable="custom" prop="impressions">
          <template slot-scope="{ row }">{{ formatInt(row.impressions) }}</template>
        </el-table-column>
        <el-table-column :label="$t('insights.clicks')" width="90" align="right" sortable="custom" prop="clicks">
          <template slot-scope="{ row }">{{ formatInt(row.clicks) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.ctr')" width="90" align="right" sortable="custom" prop="ctr">
          <template slot-scope="{ row }">{{ formatPercent(row.ctr) }}</template>
        </el-table-column>
        <el-table-column :label="$t('strategyAnalysis.cpm')" width="100" align="right" sortable="custom" prop="cpm">
          <template slot-scope="{ row }">{{ formatMoney(row.cpm) }}</template>
        </el-table-column>
        <el-table-column :label="$t('strategyAnalysis.cpc')" width="100" align="right" sortable="custom" prop="cpc">
          <template slot-scope="{ row }">{{ formatMoney(row.cpc) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.purchases')" width="80" align="right" sortable="custom" prop="purchases">
          <template slot-scope="{ row }">{{ formatInt(row.purchases) }}</template>
        </el-table-column>
        <el-table-column :label="$t('strategyAnalysis.costPerPurchaseShort')" width="100" align="right" sortable="custom" prop="costPerPurchase">
          <template slot-scope="{ row }">{{ formatMoney(row.costPerPurchase) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.purchaseValue')" width="110" align="right" sortable="custom" prop="purchaseValue">
          <template slot-scope="{ row }">{{ formatMoney(row.purchaseValue) }}</template>
        </el-table-column>
        <el-table-column :label="$t('bookSummary.roi')" width="80" align="right" sortable="custom" prop="roi">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi) }}</template>
        </el-table-column>
        <el-table-column v-if="materialsFeatureEnabled" :label="$t('common.operation')" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" :disabled="materialsLoading" @click="openMaterialsDialog(row)">{{ $t('strategyAnalysis.linkedMaterials') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <template v-if="materialsFeatureEnabled">
      <!-- 素材明细：与「书籍汇总」同款卡片网格 + 预览 -->
      <el-dialog
        :title="materialsDialogTitle"
        :visible.sync="materialsDialogVisible"
        width="960px"
        top="5vh"
        append-to-body
        custom-class="strategy-materials-dialog"
        :destroy-on-close="true"
        @closed="onMaterialsDialogClosed"
      >
        <div v-if="materialsList.length > 0" class="materials-dialog-sorter">
          <span class="sorter-label">{{ $t('strategyAnalysis.sortBy') }}</span>
          <el-select v-model="materialsSortKey" size="mini" style="width: 130px">
            <el-option :label="$t('bookSummary.spend')" value="spend" />
            <el-option :label="$t('bookSummary.roi')" value="roi" />
            <el-option :label="$t('strategyAnalysis.sortOptionCtr')" value="ctr" />
            <el-option :label="$t('bookSummary.purchaseValue')" value="purchase_value" />
            <el-option :label="$t('strategyAnalysis.impressions')" value="impressions" />
            <el-option :label="$t('insights.clicks')" value="clicks" />
            <el-option :label="$t('bookSummary.purchases')" value="purchases" />
          </el-select>
          <el-radio-group v-model="materialsSortDesc" size="mini">
            <el-radio-button :label="true">{{ $t('common.sortDesc') }}</el-radio-button>
            <el-radio-button :label="false">{{ $t('common.sortAsc') }}</el-radio-button>
          </el-radio-group>
        </div>
        <div v-loading="materialsLoading">
          <div v-if="materialsCardItems.length > 0" class="material-card-grid">
            <div
              v-for="(item, idx) in materialsCardItems"
              :key="'m-' + (item.materialId != null ? item.materialId : idx) + '-' + idx"
              class="material-card"
            >
              <div class="card-media" @click="handleMaterialsPreview(item)">
                <img
                  v-if="item.type === 'image' && item.displaySrc"
                  :src="item.displaySrc"
                  class="card-thumb"
                  alt=""
                />
                <img
                  v-else-if="item.type === 'video' && item.thumbSrc"
                  :src="item.thumbSrc"
                  class="card-thumb"
                  alt=""
                />
                <div v-else class="card-thumb-placeholder">
                  <i :class="item.type === 'video' ? 'el-icon-video-camera' : 'el-icon-picture-outline'"></i>
                </div>
                <span class="card-rank">#{{ idx + 1 }}</span>
                <span v-if="item.type === 'video'" class="video-badge">
                  <i class="el-icon-video-play"></i>
                </span>
              </div>
              <div class="card-body">
                <div class="card-name" :title="item.name">{{ item.name }}</div>
                <div v-if="item.tagNames.length > 0" class="card-tags">
                  <el-tag
                    v-for="tag in item.tagNames"
                    :key="tag"
                    size="mini"
                    effect="plain"
                    class="card-tag"
                  >{{ tag }}</el-tag>
                </div>
                <div class="card-metric-grid">
                  <div class="metric-item">
                    <span class="metric-label">{{ $t('bookSummary.spend') }}</span>
                    <span class="metric-value">{{ formatCardPlainNumber(item.spend) }}</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">{{ $t('strategyAnalysis.ctrInCard') }}</span>
                    <span class="metric-value">{{ formatPercent(item.ctr) }}</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">{{ $t('bookSummary.purchases') }}</span>
                    <span class="metric-value">{{ formatInt(item.purchases) }}</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">{{ $t('strategyAnalysis.costPerPurchaseCard') }}</span>
                    <span class="metric-value">{{ formatCardPlainNumber(item.costPerPurchase) }}</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">{{ $t('bookSummary.purchaseValue') }}</span>
                    <span class="metric-value">{{ formatCardPlainNumber(item.purchaseValue) }}</span>
                  </div>
                  <div class="metric-item metric-roi">
                    <span class="metric-label">{{ $t('bookSummary.roi') }}</span>
                    <span class="metric-value roi-value">{{ formatCardRoi(item.roi) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <el-empty v-else-if="!materialsLoading" :description="$t('strategyAnalysis.noMaterials')" />
        </div>
        <div class="materials-dialog-footer-row">
          <div v-if="materialsList.length > 0" class="summary-row materials-page-summary">
            <span class="summary-label">{{ $t('strategyAnalysis.pageSummary', { count: materialsList.length }) }}</span>
            <span>{{ $t('bookSummary.spend') }} <b>{{ formatMoney(materialsPageSummary.spend) }}</b></span>
            <span>{{ $t('strategyAnalysis.impressions') }} <b>{{ formatInt(materialsPageSummary.impressions) }}</b></span>
            <span>{{ $t('insights.clicks') }} <b>{{ formatInt(materialsPageSummary.clicks) }}</b></span>
            <span>{{ $t('bookSummary.purchases') }} <b>{{ formatInt(materialsPageSummary.purchases) }}</b></span>
            <span>{{ $t('bookSummary.purchaseValue') }} <b>{{ formatMoney(materialsPageSummary.purchaseValue) }}</b></span>
          </div>
          <el-pagination
            class="materials-pagination"
            small
            background
            layout="total, prev, pager, next"
            :disabled="materialsLoading"
            :current-page="materialsPageNo"
            :page-size="materialsPageSize"
            :total="materialsTotal"
            @current-change="handleMaterialsPageChange"
          />
        </div>
        <span slot="footer"><el-button size="small" @click="materialsDialogVisible = false">{{ $t('common.close') }}</el-button></span>
      </el-dialog>

      <el-dialog :visible.sync="materialsPreviewVisible" append-to-body width="auto" top="5vh" class="materials-preview-dialog">
        <img v-if="materialsPreviewType === 'image'" :src="materialsPreviewUrl" style="max-width: 100%; max-height: 80vh;" alt="" />
        <video v-else :src="materialsPreviewUrl" controls autoplay style="max-width: 100%; max-height: 80vh;" />
      </el-dialog>
      </template>

      <!-- 分页 -->
      <pagination v-show="total > 0" :total="total" :page.sync="pageNo" :limit.sync="pageSize" @pagination="fetchData" />

      <!-- 汇总行 -->
      <div class="summary-row" v-if="tableData.length > 0">
        <span class="summary-label">{{ $t('bookSummary.summaryLabel', { total: total }) }}</span>
        <span>{{ $t('bookSummary.spend') }} <b>{{ formatMoney(summary.spend) }}</b></span>
        <span>{{ $t('strategyAnalysis.impressions') }} <b>{{ formatInt(summary.impressions) }}</b></span>
        <span>{{ $t('insights.clicks') }} <b>{{ formatInt(summary.clicks) }}</b></span>
        <span>{{ $t('bookSummary.purchases') }} <b>{{ formatInt(summary.purchases) }}</b></span>
        <span>{{ $t('bookSummary.purchaseValue') }} <b>{{ formatMoney(summary.purchaseValue) }}</b></span>
      </div>
    </el-card>
  </div>
</template>

<script>
import { queryInsightsAggPage, exportInsightsAgg, queryCreatorOptions } from '@/api/report/insights';
import axios from 'axios';
import { listStrategies, getMaterialsForRow } from '@/api/report/strategyAnalysis';
import { withLocalDevLongRangeDateShortcuts } from '@/utils/dateRangePickerOptions';
import Pagination from '@/components/Pagination';

export default {
  name: 'StrategyAnalysis',
  components: { Pagination },
  data: function() {
    return {
      loading: false,
      activeDimension: 'strategy',
      strategyList: [],
      creatorOptions: [],
      batchSuccessCountMin: undefined,
      batchSuccessCountMax: undefined,
      dateRange: [],
      showMetricFilter: false,
      ctrMinPct: undefined,
      ctrMaxPct: undefined,
      query: {
        strategyId: undefined,
        userName: undefined,
        adAccountId: undefined,
        campaignKeyword: undefined,
        bookName: undefined,
        adsetKeyword: undefined,
        adKeyword: undefined,
        startDate: undefined,
        endDate: undefined,
        spendMin: undefined,
        spendMax: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      },
      pageNo: 1,
      pageSize: 10,
      total: 0,
      tableData: [],
      globalSummary: null,
      sortField: 'spend',
      sortOrder: 'desc',
      exporting: false,
      /** 关联素材：false 时隐藏入口与弹窗；需恢复时改为 true */
      materialsFeatureEnabled: false,
      materialsDialogVisible: false,
      materialsDialogTitle: '',
      materialsLoading: false,
      materialsList: [],
      materialsSortKey: 'spend',
      materialsSortDesc: true,
      materialsPageNo: 1,
      materialsPageSize: 10,
      materialsTotal: 0,
      materialsDialogRow: null,
      materialsDialogStrategyId: null,
      materialsCancelSource: null,
      materialsPreviewVisible: false,
      materialsPreviewUrl: '',
      materialsPreviewType: 'image',
      /** 命名策略下拉：本地搜索关键字 */
      strategySelectQuery: '',
      /** 切换维度时取消未完成的 insights 请求，避免汇总与表格错位 */
      _insightsCancelSource: null
    };
  },
  computed: {
    isDataLimited() {
      var appVm = this.$root && this.$root.$children && this.$root.$children[0];
      var user = appVm && appVm.currentUser;
      if (!user || !user.roleCodes) return false;
      return user.roleCodes.indexOf('data_limited') !== -1;
    },
    datePickerOptions: function() {
      return withLocalDevLongRangeDateShortcuts([
        { text: this.$t('insights.last7days'), onClick: function(picker) { var e = new Date(); var s = new Date(); s.setDate(s.getDate() - 7); picker.$emit('pick', [s, e]); } },
        { text: this.$t('insights.last14days'), onClick: function(picker) { var e = new Date(); var s = new Date(); s.setDate(s.getDate() - 14); picker.$emit('pick', [s, e]); } },
        { text: this.$t('insights.last30days'), onClick: function(picker) { var e = new Date(); var s = new Date(); s.setDate(s.getDate() - 30); picker.$emit('pick', [s, e]); } }
      ]);
    },
    filteredStrategyList: function() {
      var self = this;
      var list = (this.strategyList || []).slice();
      list.sort(function(a, b) {
        var ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        var tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      var q = (this.strategySelectQuery || '').trim().toLowerCase();
      return list.filter(function(s) {
        var n = s.batchSuccessCount || 0;
        if (self.batchSuccessCountMin != null && n < self.batchSuccessCountMin) return false;
        if (self.batchSuccessCountMax != null && n > self.batchSuccessCountMax) return false;
        if (q) {
          var name = (s.strategyName || '').toLowerCase();
          var kw = (s.keyword || '').toLowerCase();
          var idStr = s.id != null ? String(s.id) : '';
          if (name.indexOf(q) === -1 && kw.indexOf(q) === -1 && idStr.indexOf(q) === -1) return false;
        }
        return true;
      });
    },
    selectedStrategy: function() {
      if (!this.query.strategyId) return null;
      for (var i = 0; i < this.strategyList.length; i++) {
        if (this.strategyList[i].id === this.query.strategyId) return this.strategyList[i];
      }
      return null;
    },
    summary: function() {
      var s = this.globalSummary;
      if (s) {
        return {
          spend: Number(s.spend) || 0,
          impressions: Number(s.impressions) || 0,
          clicks: Number(s.clicks) || 0,
          purchases: Number(s.purchases) || 0,
          purchaseValue: Number(s.purchase_value) || 0
        };
      }
      var spend = 0, impressions = 0, clicks = 0, purchases = 0, purchaseValue = 0;
      for (var i = 0; i < this.tableData.length; i++) {
        var row = this.tableData[i];
        spend += Number(row.spend) || 0;
        impressions += Number(row.impressions) || 0;
        clicks += Number(row.clicks) || 0;
        purchases += Number(row.purchases) || 0;
        purchaseValue += Number(row.purchaseValue) || 0;
      }
      return { spend: spend, impressions: impressions, clicks: clicks, purchases: purchases, purchaseValue: purchaseValue };
    },
    /** 关联素材弹窗：当前页明细合计 */
    materialsPageSummary: function() {
      var spend = 0;
      var impressions = 0;
      var clicks = 0;
      var purchases = 0;
      var purchaseValue = 0;
      for (var i = 0; i < this.materialsList.length; i++) {
        var r = this.materialsList[i];
        spend += Number(r.spend) || 0;
        impressions += Number(r.impressions) || 0;
        clicks += Number(r.clicks) || 0;
        purchases += Number(r.purchases) || 0;
        purchaseValue += Number(r.purchase_value) || 0;
      }
      return { spend: spend, impressions: impressions, clicks: clicks, purchases: purchases, purchaseValue: purchaseValue };
    },
    /** 卡片展示用：排序后映射为书籍汇总同款结构 */
    materialsCardItems: function() {
      var self = this;
      var key = this.materialsSortKey || 'spend';
      var desc = this.materialsSortDesc !== false;
      var rows = (this.materialsList || []).slice();
      rows.sort(function(a, b) {
        var va = Number(a[key]);
        var vb = Number(b[key]);
        if (isNaN(va)) va = 0;
        if (isNaN(vb)) vb = 0;
        return desc ? (vb - va) : (va - vb);
      });
      return rows.map(function(row) {
        var tagNames = [];
        var tn = row.tag_names;
        if (tn != null && String(tn).trim()) {
          tagNames = String(tn).split(',').map(function(s) { return s.trim(); }).filter(Boolean);
        }
        var type = String(row.type || 'image').toLowerCase() === 'video' ? 'video' : 'image';
        var filePath = self.resolveMaterialUrl(row.file_path);
        var thumbnail = self.resolveMaterialUrl(row.thumbnail);
        return {
          materialId: row.material_id,
          name: row.material_name || '',
          type: type,
          filePath: filePath,
          displaySrc: type === 'image' ? (filePath || thumbnail) : (thumbnail || filePath),
          thumbSrc: thumbnail || filePath,
          spend: row.spend,
          ctr: row.ctr,
          purchases: row.purchases,
          costPerPurchase: row.cost_per_purchase,
          purchaseValue: row.purchase_value,
          roi: row.roi,
          tagNames: tagNames
        };
      });
    }
  },
  created: function() {
    var end = new Date();
    var start = new Date();
    start.setDate(start.getDate() - 6);
    var fmt = function(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
    this.dateRange = [fmt(start), fmt(end)];
    this.query.startDate = fmt(start);
    this.query.endDate = fmt(end);
    this.loadFilterOptions();
    this.handleQuery();
  },
  methods: {
    /** 下拉选中后仅展示策略名称（不拼接 keyword，避免与选项重复、观感杂乱） */
    strategyOptionLabel: function(s) {
      if (!s) return '';
      return s.strategyName || '';
    },
    filterStrategySelect: function(val) {
      this.strategySelectQuery = val || '';
    },
    onStrategySelectVisible: function(open) {
      if (!open) this.strategySelectQuery = '';
    },
    loadFilterOptions: function() {
      listStrategies().then(function(res) { this.strategyList = res.data || []; }.bind(this));
      queryCreatorOptions().then(function(res) { this.creatorOptions = res.data || []; }.bind(this));
    },
    fetchData: function() {
      if (this._insightsCancelSource) {
        this._insightsCancelSource.cancel('replaced');
      }
      this._insightsCancelSource = axios.CancelToken.source();
      this.loading = true;
      var params = this.buildParams();
      params.pageNo = this.pageNo;
      params.pageSize = this.pageSize;
      var cancelToken = this._insightsCancelSource.token;
      queryInsightsAggPage(Object.assign({}, params, { cancelToken: cancelToken })).then(function(res) {
        var pageData = res.data || {};
        this.tableData = pageData.records || [];
        this.total = pageData.total || 0;
        this.globalSummary = pageData.summary || null;
        this.loading = false;
      }.bind(this)).catch(function(err) {
        if (axios.isCancel(err)) {
          return;
        }
        this.loading = false;
      }.bind(this));
    },
    resolveKeyword: function(keyword) {
      if (!keyword || !keyword.trim()) return { id: undefined, name: undefined };
      var v = keyword.trim();
      if (/^\d+$/.test(v)) return { id: v, name: undefined };
      return { id: undefined, name: v };
    },
    buildParams: function() {
      var q = this.query;
      var campaign = this.resolveKeyword(q.campaignKeyword);
      var adset = this.resolveKeyword(q.adsetKeyword);
      var ad = this.resolveKeyword(q.adKeyword);
      var params = {
        dimension: this.activeDimension,
        startDate: q.startDate,
        endDate: q.endDate,
        /** 策略分析全模块只看待绑定命名策略的消耗，排除维表 strategy_id 为空/0 的系列 */
        requireStrategyBound: true,
        strategyId: q.strategyId,
        userName: q.userName,
        adAccountId: q.adAccountId,
        campaignId: campaign.id,
        campaignName: campaign.name,
        bookName: q.bookName,
        adsetId: adset.id,
        adsetName: adset.name,
        adId: ad.id,
        adNameKeyword: ad.name,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        spendMin: q.spendMin,
        spendMax: q.spendMax,
        purchasesMin: q.purchasesMin,
        purchasesMax: q.purchasesMax,
        costPerPurchaseMin: q.costPerPurchaseMin,
        costPerPurchaseMax: q.costPerPurchaseMax,
        purchaseValueMin: q.purchaseValueMin,
        purchaseValueMax: q.purchaseValueMax,
        roiMin: q.roiMin,
        roiMax: q.roiMax
      };
      if (this.ctrMinPct != null) params.ctrMin = this.ctrMinPct / 100;
      if (this.ctrMaxPct != null) params.ctrMax = this.ctrMaxPct / 100;
      return params;
    },
    handleQuery: function() {
      this.pageNo = 1;
      this.fetchData();
    },
    resetQuery: function() {
      var end = new Date();
      var start = new Date();
      start.setDate(start.getDate() - 6);
      var fmt = function(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
      this.dateRange = [fmt(start), fmt(end)];
      this.query = {
        strategyId: undefined,
        userName: undefined,
        adAccountId: undefined,
        campaignKeyword: undefined,
        bookName: undefined,
        adsetKeyword: undefined,
        adKeyword: undefined,
        startDate: fmt(start),
        endDate: fmt(end),
        spendMin: undefined,
        spendMax: undefined,
        purchasesMin: undefined,
        purchasesMax: undefined,
        costPerPurchaseMin: undefined,
        costPerPurchaseMax: undefined,
        purchaseValueMin: undefined,
        purchaseValueMax: undefined,
        roiMin: undefined,
        roiMax: undefined
      };
      this.ctrMinPct = undefined;
      this.ctrMaxPct = undefined;
      this.batchSuccessCountMin = undefined;
      this.batchSuccessCountMax = undefined;
      this.pageNo = 1;
      this.sortField = 'spend';
      this.sortOrder = 'desc';
      this.tableData = [];
      this.total = 0;
      this.globalSummary = null;
    },
    handleDateChange: function(val) {
      if (val && val.length === 2) {
        this.query.startDate = val[0];
        this.query.endDate = val[1];
      }
    },
    handleTabChange: function() {
      this.pageNo = 1;
      this.sortField = 'spend';
      this.sortOrder = 'desc';
      this.fetchData();
    },
    handleSortChange: function(obj) {
      this.sortField = obj.prop || 'spend';
      this.sortOrder = obj.order === 'ascending' ? 'asc' : 'desc';
      this.pageNo = 1;
      this.fetchData();
    },
    cancelMaterialsRequest: function() {
      if (this.materialsCancelSource) {
        this.materialsCancelSource.cancel('materials-dialog');
        this.materialsCancelSource = null;
      }
    },
    onMaterialsDialogClosed: function() {
      this.cancelMaterialsRequest();
      this.materialsDialogRow = null;
      this.materialsDialogStrategyId = null;
      this.materialsPageNo = 1;
      this.materialsTotal = 0;
      this.materialsList = [];
      this.materialsPreviewVisible = false;
      this.materialsPreviewUrl = '';
    },
    openMaterialsDialog: function(row) {
      if (!this.materialsFeatureEnabled) return;
      var sid = row.namingStrategyId != null && String(row.namingStrategyId).trim() !== ''
        ? String(row.namingStrategyId).trim()
        : (this.query.strategyId != null ? String(this.query.strategyId) : '');
      if (!sid || sid === '0') {
        this.$message.warning(this.$t('strategyAnalysis.warnNoStrategyContext'));
        return;
      }
      var sidNum = Number(sid);
      if (!Number.isFinite(sidNum) || sidNum <= 0) {
        this.$message.warning(this.$t('strategyAnalysis.warnInvalidStrategyId'));
        return;
      }
      this.cancelMaterialsRequest();
      this.materialsCancelSource = axios.CancelToken.source();
      this.materialsDialogRow = row;
      this.materialsDialogStrategyId = sidNum;
      this.materialsPageNo = 1;
      var dateInfo = (this.query.startDate && this.query.endDate)
        ? ' (' + this.query.startDate + ' ~ ' + this.query.endDate + ')'
        : '';
      var sub = row.bookName || row.book_name || row.campaignName || row.campaign_name
        || row.adAccountName || row.adAccountId || row.strategyName || this.$t('strategyAnalysis.currentRowFallback');
      this.materialsDialogTitle = this.$t('bookSummary.materialDialogTitle') + sub + dateInfo;
      this.materialsSortKey = 'spend';
      this.materialsSortDesc = true;
      this.materialsDialogVisible = true;
      this.loadMaterialsForRow();
    },
    handleMaterialsPreview: function(item) {
      var url = item.type === 'video' ? item.filePath : (item.filePath || item.displaySrc);
      if (!url) return;
      this.materialsPreviewType = item.type || 'image';
      this.materialsPreviewUrl = url;
      this.materialsPreviewVisible = true;
    },
    resolveMaterialUrl: function(raw) {
      if (raw == null) return '';
      var s = String(raw).trim();
      if (!s) return '';
      if (/^https?:\/\//i.test(s)) return s;
      if (s.charAt(0) === '/') return s;
      return s;
    },
    formatCardPlainNumber: function(v) {
      if (v == null || v === '' || isNaN(Number(v))) return '-';
      return Number(v).toFixed(2);
    },
    formatCardRoi: function(v) {
      if (v == null || v === '' || isNaN(Number(v))) return '-';
      return Number(v).toFixed(4);
    },
    handleMaterialsPageChange: function(p) {
      this.materialsPageNo = p;
      this.cancelMaterialsRequest();
      this.materialsCancelSource = axios.CancelToken.source();
      this.loadMaterialsForRow();
    },
    loadMaterialsForRow: function() {
      var row = this.materialsDialogRow;
      var strategyIdNum = this.materialsDialogStrategyId;
      if (!row || strategyIdNum == null) return;
      this.materialsLoading = true;
      this.materialsList = [];
      var params = {
        startDate: this.query.startDate,
        endDate: this.query.endDate,
        strategyId: strategyIdNum,
        userName: this.query.userName || undefined,
        adAccountId: row.adAccountId || undefined,
        campaignId: row.campaignId || undefined,
        adsetId: row.adsetId || undefined,
        adId: row.adId || undefined,
        pageNo: this.materialsPageNo,
        pageSize: this.materialsPageSize,
        cancelToken: this.materialsCancelSource ? this.materialsCancelSource.token : undefined
      };
      var self = this;
      getMaterialsForRow(params).then(function(res) {
        var d = res.data || {};
        self.materialsList = d.records || [];
        self.materialsTotal = Number(d.total) || 0;
      }).catch(function(err) {
        if (axios.isCancel && axios.isCancel(err)) {
          return;
        }
        self.materialsList = [];
        self.materialsTotal = 0;
        self.$message.error(self.$t('strategyAnalysis.loadMaterialsFail'));
      }).then(function() {
        self.materialsLoading = false;
      });
    },
    handleExport: function() {
      this.$confirm(this.$t('strategyAnalysis.exportConfirm'), this.$t('strategyAnalysis.exportTipTitle'), {
        confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning'
      }).then(function() {
        this.exporting = true;
        var params = this.buildParams();
        exportInsightsAgg(params).then(function(res) {
          var blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          var url = window.URL.createObjectURL(blob);
          var link = document.createElement('a');
          link.href = url;
          link.download = this.$t('strategyAnalysis.exportFileName');
          link.click();
          window.URL.revokeObjectURL(url);
          this.$message.success(this.$t('strategyAnalysis.exportSuccess'));
          this.exporting = false;
        }.bind(this)).catch(function() {
          this.$message.error(this.$t('strategyAnalysis.exportFail'));
          this.exporting = false;
        }.bind(this));
      }.bind(this)).catch(function() {});
    },
    formatMoney: function(v) { if (v == null || isNaN(v)) return '$0.00'; return '$' + Number(v).toFixed(2); },
    formatInt: function(v) { if (v == null || isNaN(v)) return '0'; return Number(v).toLocaleString(); },
    formatPercent: function(v) { if (v == null || isNaN(v)) return '0.00%'; return (Number(v) * 100).toFixed(2) + '%'; },
    formatDecimal: function(v) { if (v == null || isNaN(v)) return '0.00'; return Number(v).toFixed(2); }
  }
}
</script>

<style scoped>
.stats-cards {
  display: flex;
  gap: 16px;
  margin: 8px 0 4px;
  flex-wrap: wrap;
}
.stats-card {
  background: #f8fafd;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 18px;
  min-width: 120px;
}
.stats-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
.stats-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.text-primary { color: #409EFF; }
.text-danger { color: #F56C6C; }
.text-success { color: #67C23A; }
.metric-filter-row {
  background: #fafbfc;
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.range-sep {
  margin: 0 4px;
  color: #999;
}
.range-unit {
  margin-left: 2px;
  color: #999;
  font-size: 12px;
}
.summary-row {
  margin-top: 12px;
  padding: 8px 16px;
  background: #fafafa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
}
.summary-row span { margin-right: 20px; }
.summary-row b { color: #303133; }
.summary-label { font-weight: 600; margin-right: 16px; }

.materials-dialog-sorter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.materials-dialog-sorter .sorter-label {
  font-size: 13px;
  color: #606266;
}
.materials-dialog-footer-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}
.materials-page-summary {
  flex: 1;
  margin-top: 0;
  min-width: 280px;
}
.materials-pagination {
  flex-shrink: 0;
}
.material-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  max-height: 72vh;
  overflow-y: auto;
  padding: 8px 4px;
}
.material-card {
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}
.material-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}
.card-media {
  position: relative;
  width: 100%;
  padding-top: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  cursor: pointer;
}
.card-thumb {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}
.card-media:hover .card-thumb {
  transform: scale(1.05);
}
.card-thumb-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #c0c4cc;
}
.card-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.video-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.card-body {
  padding: 12px 14px 14px;
}
.card-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  line-height: 1.4;
}
.card-tags {
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.card-tag {
  border-radius: 3px;
}
.card-metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}
.metric-item {
  display: flex;
  flex-direction: column;
}
.metric-label {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
}
.metric-value {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}
.metric-roi {
  grid-column: 1 / -1;
  border-top: 1px solid #f0f2f5;
  padding-top: 6px;
  margin-top: 2px;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}
.roi-value {
  font-size: 16px;
  color: #e6a23c;
}
</style>

<style>
.strategy-materials-dialog .el-dialog__body {
  padding: 12px 20px 8px;
}
.materials-preview-dialog .el-dialog__body {
  text-align: center;
  padding: 10px;
}
</style>
